import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBooking } from "../data/bookings";
import { getTutorById } from "../data/tutors";
import { toId } from "../lib/subjects";

export default function BookingSuccess() {
  const [p] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language || "en";
  const [loading, setLoading] = useState(true);
const [err, setErr] = useState(null);

  const id = p.get("id");

  const [b, setB] = useState(null);
  const [tutor, setTutor] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!id) {
        if (alive) {
          setErr(t("bookingSuccess.notFound"));
          setLoading(false);
        }
        return;
      }

      try {
        const booking = await getBooking(id);
        if (!alive) return;
        setB(booking);

        // подгружаем репетитора из API
        if (booking?.tutorId) {
          const tu = await getTutorById(booking.tutorId);
          if (!alive) return;
          setTutor(tu);
        }
      } catch (e) {
        console.error("BookingSuccess load error:", e);
        if (alive) {
          setB(null);
          setTutor(null);
          setErr(t("bookings.errors.load"));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const tutorName =
    tutor?.profile?.displayName || tutor?.profile?.name || b?.tutorName || "";

  const subjectLabel = b
    ? t(`subjects.${toId(b.subject)}`, { defaultValue: b.subject })
    : "";

  const modeKey = b ? (b.mode === "in-person" ? "inPerson" : (b.mode || "online")) : "online";
  const modeLabel = t(`modes.${modeKey}`, { defaultValue: b?.mode || "online" });
  const statusLabel = b ? t(`statuses.${b.status}`, { defaultValue: b.status }) : "";

  const fmt = (iso, lng) =>
  new Intl.DateTimeFormat(lng, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h1 className="text-2xl font-semibold">{t("bookingSuccess.title")} 🎉</h1>
          <p className="mt-2 text-white/80">
            {t("bookingSuccess.subtitle")}{" "}
            <span className="font-medium">{t("bookings.title")}</span>.
          </p>

          {loading ? (
            <div className="mt-6 text-white/70">Loading…</div>
          ) : err ? (
            <div className="mt-6 text-white/70">
              {err}{" "}
              <button
                className="underline hover:no-underline"
                onClick={() => navigate("/search")}
              >
                {t("bookingSuccess.findAnother")}
              </button>
            </div>
          ) : b ? (
            <div className="mt-6 space-y-2 text-white/90">
              <div>
                <span className="text-white/60">{t("bookingSuccess.labels.tutor")}:</span>{" "}
                {tutorName}
              </div>
              <div>
                <span className="text-white/60">{t("bookingSuccess.labels.subject")}:</span>{" "}
                {subjectLabel}
              </div>
              <div>
                <span className="text-white/60">{t("bookingSuccess.labels.when")}:</span>{" "}
                {b?.preferredAt ? fmt(b.preferredAt, lng) : t("common.notSpecified")}
              </div>
              <div>
                <span className="text-white/60">{t("bookingSuccess.labels.mode")}:</span>{" "}
                {modeLabel}
              </div>
              <div>
                <span className="text-white/60">{t("bookingSuccess.labels.rate")}:</span>{" "}
                {Number.isFinite(Number(b?.rate)) ? `${Number(b.rate)}` : t("common.notSpecified")}
              </div>
              <div>
                <span className="text-white/60">{t("bookingSuccess.labels.status")}:</span>{" "}
                {statusLabel}
              </div>
            </div>
          ) : (
            <div className="mt-6 text-white/70">
              {t("bookingSuccess.notFound")}{" "}
              <button
                className="underline hover:no-underline"
                onClick={() => navigate("/search")}
              >
                {t("bookingSuccess.findAnother")}
              </button>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            <Link
              to="/bookings"
              className="rounded-xl bg-white/10 px-4 py-2 hover:bg-white/15"
            >
              {t("bookingSuccess.goToBookings")}
            </Link>
            <Link
              to="/dashboard"
              className="rounded-xl border border-white/20 px-4 py-2 hover:bg-white/10"
            >
              {t("bookingSuccess.backToDashboard")}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
